import { useCallback, useEffect, useMemo, useState } from "react";
import type { AgendaRoom } from "@domaddress/shared";
import type { CalendarItem, GoogleCalendarMeta } from "../lib/calendarTypes";
import { blockDateTimeRange } from "../lib/calendarMatch";
import {
  addDays,
  formatDateKey,
  formatTimeNl,
  formatTimeRange,
  getMonthGridCells,
  getMonthName,
  getWeekMonday,
  getWeekdayShort,
  itemTouchesDateKey,
  parseDateTime,
} from "../lib/calendarDate";

const DEFAULT_OFF_ROOMS = ["Keuken", "Pastoraatskamer"];
const PX_PER_HOUR = 48;
const HOUR_END = 24;

type ViewMode = "month" | "week" | "list";

interface Props {
  items: CalendarItem[];
  rooms: AgendaRoom[];
  googleCalendars: GoogleCalendarMeta[];
  loading: boolean;
  currentDate: Date;
  onCurrentDateChange: (d: Date) => void;
  viewMode: ViewMode;
  onViewModeChange: (m: ViewMode) => void;
}

function eventCategoryClass(category: string | null): string {
  if (category === "extern") return "event-extern";
  if (category === "speciale-dienst") return "event-speciale-dienst";
  return "event-default";
}

function calendarItemClass(it: CalendarItem): string {
  if (it.kind === "linked") return "event-linked";
  if (it.kind === "google") return "event-google";
  return eventCategoryClass(it.category);
}

function isRecurringItem(it: CalendarItem): boolean {
  return (it.recurrence_type ?? "none") !== "none";
}

function RecurrenceBadge() {
  return (
    <span className="event-recurrence-badge" title="Terugkerend evenement" aria-hidden>
      ↻
    </span>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function initRoomVisibility(rooms: AgendaRoom[]): Record<number, boolean> {
  const vis: Record<number, boolean> = {};
  for (const r of rooms) {
    vis[r.id] = !DEFAULT_OFF_ROOMS.includes(r.name);
  }
  return vis;
}

function initGoogleVisibility(cals: GoogleCalendarMeta[]): Record<string, boolean> {
  const vis: Record<string, boolean> = {};
  for (const c of cals) {
    // Hide Keuken and Pastoraatskamer by default (same as internal rooms)
    vis[c.id] = !DEFAULT_OFF_ROOMS.includes(c.name);
  }
  return vis;
}

export default function AgendaCalendar({
  items,
  rooms,
  googleCalendars,
  loading,
  currentDate,
  onCurrentDateChange,
  viewMode,
  onViewModeChange,
}: Props) {
  const [roomVisible, setRoomVisible] = useState<Record<number, boolean>>({});
  const [googleVisible, setGoogleVisible] = useState<Record<string, boolean>>({});
  const [showEvents, setShowEvents] = useState(true);
  const [search, setSearch] = useState("");
  const [popup, setPopup] = useState<CalendarItem | null>(null);

  useEffect(() => {
    if (rooms.length === 0) return;
    setRoomVisible((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      return initRoomVisibility(rooms);
    });
  }, [rooms]);

  useEffect(() => {
    if (googleCalendars.length === 0) return;
    setGoogleVisible((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      return initGoogleVisibility(googleCalendars);
    });
  }, [googleCalendars]);

  const searchLower = search.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (it.kind === "event") {
        if (!showEvents) return false;
      } else if (it.kind === "linked") {
        if (!showEvents) return false;
        if (it.google_calendar_id != null && googleVisible[it.google_calendar_id] === false)
          return false;
      } else if (it.kind === "google") {
        if (it.google_calendar_id != null && googleVisible[it.google_calendar_id] === false)
          return false;
      } else if (it.room_id != null && roomVisible[it.room_id] === false) {
        return false;
      }
      if (searchLower && !it.title.toLowerCase().includes(searchLower)) return false;
      return true;
    });
  }, [items, showEvents, roomVisible, googleVisible, searchLower]);

  const bookingsByDate = useMemo(() => {
    const map: Record<string, CalendarItem[]> = {};
    for (const it of filteredItems) {
      if (it.kind !== "event" && it.kind !== "linked") continue;
      const roomList = it.rooms ?? [];
      for (const room of roomList) {
        if (roomVisible[room.room_id] === false) continue;
        const strip: CalendarItem = {
          ...it,
          start_datetime: room.start_datetime,
          end_datetime: room.end_datetime,
          room_id: room.room_id,
          room_name: room.name,
          room_color: room.color,
          google_calendar_id: room.google_calendar_id,
        };
        const start = parseDateTime(strip.start_datetime);
        const end = parseDateTime(strip.end_datetime);
        for (const key of dateKeysSpan(start, end)) {
          if (!map[key]) map[key] = [];
          map[key]!.push(strip);
        }
      }
    }
    for (const key of Object.keys(map)) {
      map[key]!.sort((a, b) => a.start_datetime.localeCompare(b.start_datetime));
    }
    return map;
  }, [filteredItems, roomVisible]);

  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarItem[]> = {};
    for (const it of filteredItems) {
      if (it.kind !== "event" && it.kind !== "google" && it.kind !== "linked") continue;
      // Gekoppeld: in maand/lijst op dagen van het evenement; weekweergave gebruikt het Google-blok
      const range =
        it.kind === "linked"
          ? { start: it.start_datetime, end: it.end_datetime }
          : blockDateTimeRange(it);
      const start = parseDateTime(range.start);
      const end = parseDateTime(range.end);
      for (const key of dateKeysSpan(start, end)) {
        if (!map[key]) map[key] = [];
        map[key]!.push(it);
      }
    }
    for (const key of Object.keys(map)) {
      map[key]!.sort((a, b) => a.start_datetime.localeCompare(b.start_datetime));
    }
    return map;
  }, [filteredItems]);

  const todayKey = formatDateKey(new Date());

  const goPrev = useCallback(() => {
    const d = new Date(currentDate);
    if (viewMode === "week") d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    onCurrentDateChange(d);
  }, [currentDate, viewMode, onCurrentDateChange]);

  const goNext = useCallback(() => {
    const d = new Date(currentDate);
    if (viewMode === "week") d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    onCurrentDateChange(d);
  }, [currentDate, viewMode, onCurrentDateChange]);

  const goToday = () => onCurrentDateChange(new Date());

  const toggleRoom = (id: number) => {
    setRoomVisible((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleGoogle = (id: string) => {
    setGoogleVisible((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const titleText =
    viewMode === "week"
      ? weekTitle(currentDate)
      : `${getMonthName(currentDate.getMonth())} ${currentDate.getFullYear()}`;

  return (
    <div className="dom-calendar-container">
      <div className="dom-calendar desktop">
        <div className="dom-calendar-header">
          <button type="button" className="dom-cal-nav-btn" onClick={goPrev} aria-label="Vorige">
            &lt;
          </button>
          <div className="dom-calendar-title-wrap">
            <span className="dom-calendar-title">{titleText}</span>
            <div className="dom-calendar-view-toggle">
              {(["month", "week", "list"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={viewMode === m}
                  onClick={() => onViewModeChange(m)}
                >
                  {m === "month" ? "Maand" : m === "week" ? "Week" : "Lijst"}
                </button>
              ))}
            </div>
          </div>
          <div className="dom-calendar-header-right">
            <button type="button" className="dom-cal-today-btn" onClick={goToday}>
              Vandaag
            </button>
            <button type="button" className="dom-cal-nav-btn" onClick={goNext} aria-label="Volgende">
              &gt;
            </button>
          </div>
        </div>

        <div className="dom-calendar-filters">
          <input
            type="search"
            className="dom-calendar-search"
            placeholder="Zoeken op titel…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label className="dom-calendar-filter-events">
            <input
              type="checkbox"
              checked={showEvents}
              onChange={(e) => setShowEvents(e.target.checked)}
            />
            <span>Evenementen</span>
          </label>
        </div>

        {/* Internal room bookings legend */}
        {rooms.length > 0 && (
          <div className="dom-calendar-legend" role="list">
            {rooms.map((room) => (
              <label
                key={room.id}
                className="dom-calendar-legend-item"
                style={{ ["--cal-color" as string]: room.color }}
              >
                <input
                  type="checkbox"
                  className="dom-calendar-legend-checkbox"
                  checked={roomVisible[room.id] !== false}
                  onChange={() => toggleRoom(room.id)}
                />
                <span className="dom-calendar-legend-name">{room.name}</span>
              </label>
            ))}
          </div>
        )}

        {/* Google Calendar legend */}
        {googleCalendars.length > 0 && (
          <div className="dom-calendar-legend dom-calendar-legend-google" role="list">
            <span className="dom-calendar-legend-label">Google Agenda</span>
            {googleCalendars.map((cal) => (
              <label
                key={cal.id}
                className="dom-calendar-legend-item"
                style={{ ["--cal-color" as string]: cal.color }}
              >
                <input
                  type="checkbox"
                  className="dom-calendar-legend-checkbox"
                  checked={googleVisible[cal.id] !== false}
                  onChange={() => toggleGoogle(cal.id)}
                />
                <span className="dom-calendar-legend-name">{cal.name}</span>
              </label>
            ))}
          </div>
        )}

        {loading && <p className="dom-calendar-loading">Agenda laden…</p>}

        {!loading && viewMode === "month" && (
          <MonthView
            anchor={currentDate}
            todayKey={todayKey}
            rooms={rooms}
            roomVisible={roomVisible}
            bookingsByDate={bookingsByDate}
            eventsByDate={eventsByDate}
            onSelect={setPopup}
          />
        )}

        {!loading && viewMode === "week" && (
          <WeekView anchor={currentDate} todayKey={todayKey} items={filteredItems} onSelect={setPopup} />
        )}

        {!loading && viewMode === "list" && (
          <ListView anchor={currentDate} items={filteredItems} onSelect={setPopup} />
        )}
      </div>

      {popup && <CalendarPopup item={popup} onClose={() => setPopup(null)} />}
    </div>
  );
}

function dateKeysSpan(start: Date, end: Date): string[] {
  const keys: string[] = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cur <= last) {
    keys.push(formatDateKey(cur));
    cur = addDays(cur, 1);
  }
  return keys;
}

function weekTitle(anchor: Date): string {
  const mon = getWeekMonday(anchor);
  const sun = addDays(mon, 6);
  return `${mon.getDate()} – ${sun.getDate()} ${getMonthName(sun.getMonth()).toLowerCase()} ${sun.getFullYear()}`;
}

function MonthView({
  anchor,
  todayKey,
  rooms,
  roomVisible,
  bookingsByDate,
  eventsByDate,
  onSelect,
}: {
  anchor: Date;
  todayKey: string;
  rooms: AgendaRoom[];
  roomVisible: Record<number, boolean>;
  bookingsByDate: Record<string, CalendarItem[]>;
  eventsByDate: Record<string, CalendarItem[]>;
  onSelect: (it: CalendarItem) => void;
}) {
  const cells = getMonthGridCells(anchor);
  const visibleRooms = rooms.filter((r) => roomVisible[r.id] !== false);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  return (
    <>
      <div className="dom-calendar-weekdays">
        {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((d) => (
          <div key={d} className="weekday">
            {d}
          </div>
        ))}
      </div>
      <div className="dom-calendar-days">
        {cells.map(({ date, dateKey, inMonth }) => {
          const isToday = dateKey === todayKey;
          const isPast = date < todayStart && !isToday;
          const dayBookings = bookingsByDate[dateKey] ?? [];
          const dayEvents = eventsByDate[dateKey] ?? [];
          const hasStrips = visibleRooms.length > 0;

          return (
            <div
              key={dateKey}
              className={[
                "dom-calendar-day",
                !inMonth ? "empty" : "",
                isPast ? "past" : "",
                isToday ? "today" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <div className="day-number">{date.getDate()}</div>
              <div className="day-cell-inner">
                {hasStrips && (
                  <div className="day-cell-external">
                    <div className="day-external-strips">
                      {visibleRooms.map((room) => {
                        const evs = dayBookings.filter((b) => b.room_id === room.id);
                        return (
                          <div key={room.id} className="day-external-strip" title={room.name}>
                            {evs.map((ev) => (
                              <button
                                key={`${ev.id}-${room.id}-${ev.start_datetime}`}
                                type="button"
                                className="day-external-dot"
                                style={{ background: room.color }}
                                title={`${room.name}\n${formatTimeRange(ev.start_datetime, ev.end_datetime)}\n${ev.title}`}
                                onClick={() => onSelect(ev)}
                              />
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="day-events">
                  {dayEvents.map((ev) => (
                    <button
                      key={`e-${ev.kind}-${ev.id}-${ev.google_calendar_id ?? ""}-${dateKey}`}
                      type="button"
                      className={`calendar-event ${calendarItemClass(ev)}`}
                      style={
                        ev.kind === "google" && ev.room_color
                          ? { borderLeftColor: ev.room_color, background: `${ev.room_color}18` }
                          : ev.kind === "linked" && ev.room_color
                            ? { borderLeftColor: ev.room_color }
                            : undefined
                      }
                      onClick={() => onSelect(ev)}
                    >
                      <div className="calendar-event-title-row">
                        {ev.kind === "linked" && <LinkIcon className="cal-link-icon" />}
                        {isRecurringItem(ev) && <RecurrenceBadge />}
                        <strong className="event-time">{formatTimeNl(ev.start_datetime)}</strong>
                        <span className="event-title"> {ev.title}</span>
                      </div>
                      {ev.room_name && (ev.kind === "google" || ev.kind === "linked") && (
                        <div className="event-location">{ev.room_name}</div>
                      )}
                      {ev.location_name && ev.kind !== "google" && (
                        <div className="event-location">{ev.location_name}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function WeekView({
  anchor,
  todayKey,
  items,
  onSelect,
}: {
  anchor: Date;
  todayKey: string;
  items: CalendarItem[];
  onSelect: (it: CalendarItem) => void;
}) {
  const monday = getWeekMonday(anchor);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(monday, i);
    return { date, dateKey: formatDateKey(date) };
  });
  const totalHeight = HOUR_END * PX_PER_HOUR;

  const dayItems = weekDays.map(({ dateKey }) =>
    items.filter((it) => {
      const range = blockDateTimeRange(it);
      return itemTouchesDateKey(range.start, range.end, dateKey);
    })
  );

  return (
    <div className="dom-calendar-week-wrap">
      <div className="dom-calendar-week-header">
        <div className="week-time-col">
          <div className="week-time-header" />
        </div>
        <div className="week-days">
          {weekDays.map(({ date, dateKey }) => {
            const isToday = dateKey === todayKey;
            return (
              <div key={dateKey} className={`week-day${isToday ? " today" : ""}`}>
                <div className="week-day-header">
                  <div className="week-day-name">{getWeekdayShort(date)}</div>
                  <div className="week-day-num">{date.getDate()}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="dom-calendar-week-scroll">
        <div className="dom-calendar-week-grid dom-calendar-week-view" style={{ height: totalHeight }}>
          <div className="week-time-col">
            {Array.from({ length: HOUR_END }, (_, h) => (
              <div key={h} className="week-time-cell" style={{ height: PX_PER_HOUR }}>
                {h}:00
              </div>
            ))}
          </div>
          <div className="week-days">
            {weekDays.map(({ dateKey }, dayIdx) => {
              const isToday = dateKey === todayKey;
              const blocks = dayItems[dayIdx] ?? [];
              return (
                <div key={dateKey} className={`week-day${isToday ? " today" : ""}`}>
                  <div className="week-day-body" style={{ height: totalHeight, position: "relative" }}>
                    {blocks.map((it) => {
                      const style = eventBlockStyle(it, dateKey);
                      const hasRoom = (it.rooms?.length ?? 0) > 0;
                      const isGoogle = it.kind === "google";
                      const isLinked = it.kind === "linked";
                      const color =
                        it.rooms?.[0]?.color ?? it.room_color ?? (hasRoom ? "#2c2185" : undefined);
                      return (
                        <button
                          key={`${it.kind}-${it.id}-${it.occurrence_date ?? ""}-${it.google_calendar_id ?? ""}-${dateKey}`}
                          type="button"
                          className={`week-event${hasRoom ? " week-event-booking" : ""}${isGoogle ? " week-event-google" : ""}${isLinked ? " week-event-linked" : ""}`}
                          style={{
                            ...style,
                            ...((hasRoom || isGoogle) && color
                              ? { background: color, borderColor: color, color: "#fff" }
                              : {}),
                            ...(isLinked
                              ? {
                                  background: `${color}22`,
                                  borderColor: "#7c3aed",
                                  color: "var(--text-900, #1a1a1a)",
                                }
                              : {}),
                          }}
                          onClick={() => onSelect(it)}
                        >
                          <span className="week-event-time">
                            {isLinked && <LinkIcon className="cal-link-icon cal-link-icon-week" />}
                            {formatTimeNl(it.start_datetime)}
                          </span>
                          <span className="week-event-title">{it.title}</span>
                          {(hasRoom || isGoogle || isLinked) && it.room_name && (
                            <span className="week-event-room">{it.room_name}</span>
                          )}
                        </button>
                      );
                    })}
                    {isToday && <NowLine />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function eventBlockStyle(it: CalendarItem, dateKey: string): React.CSSProperties {
  const dayStart = new Date(`${dateKey}T00:00:00`);
  const range = blockDateTimeRange(it);
  const start = parseDateTime(range.start);
  const end = parseDateTime(range.end);
  const clipStart = start < dayStart ? dayStart : start;
  const dayEnd = new Date(`${dateKey}T23:59:59`);
  const clipEnd = end > dayEnd ? dayEnd : end;
  const startMin = clipStart.getHours() * 60 + clipStart.getMinutes();
  const endMin = clipEnd.getHours() * 60 + clipEnd.getMinutes();
  const top = (startMin / 60) * PX_PER_HOUR;
  const height = Math.max(((endMin - startMin) / 60) * PX_PER_HOUR, 20);
  return { position: "absolute", left: 2, right: 2, top, height, zIndex: 2 };
}

function NowLine() {
  const now = new Date();
  const top = ((now.getHours() * 60 + now.getMinutes()) / 60) * PX_PER_HOUR;
  return <div className="week-now-line" style={{ top }} />;
}

function ListView({
  anchor,
  items,
  onSelect,
}: {
  anchor: Date;
  items: CalendarItem[];
  onSelect: (it: CalendarItem) => void;
}) {
  const cells = getMonthGridCells(anchor);
  const from = cells[0]!.dateKey;
  const to = cells[cells.length - 1]!.dateKey;
  const sorted = [...items].sort((a, b) => a.start_datetime.localeCompare(b.start_datetime));

  if (sorted.length === 0) {
    return <p className="dom-calendar-list-empty">Geen items in deze periode.</p>;
  }

  let lastKey = "";
  return (
    <ul className="dom-calendar-list">
      {sorted.map((it, idx) => {
        const key = formatDateKey(parseDateTime(it.start_datetime));
        const showHeader = key !== lastKey && key >= from && key <= to;
        lastKey = key;
        const dotColor =
          it.kind === "linked" ? "#7c3aed" : (it.room_color ?? (it.kind === "event" ? "#e2a922" : "#999"));
        return (
          <li key={`${it.kind}-${it.id}-${it.google_calendar_id ?? ""}-${idx}`}>
            {showHeader && (
              <div className="dom-calendar-list-day">
                {new Date(`${key}T12:00:00`).toLocaleDateString("nl-NL", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
              </div>
            )}
            <button
              type="button"
              className={`dom-calendar-list-item${it.kind === "linked" ? " dom-calendar-list-item-linked" : ""}`}
              onClick={() => onSelect(it)}
            >
              {it.kind === "linked" && <LinkIcon className="cal-link-icon cal-link-icon-list" />}
              {isRecurringItem(it) && <RecurrenceBadge />}
              <span className="dom-calendar-list-dot" style={{ background: dotColor }} />
              <span className="dom-calendar-list-time">
                {formatTimeRange(it.start_datetime, it.end_datetime)}
              </span>
              <span className="dom-calendar-list-title">{it.title}</span>
              {it.room_name && <span className="dom-calendar-list-meta">{it.room_name}</span>}
              {it.location_name && (
                <span className="dom-calendar-list-meta">{it.location_name}</span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function CalendarPopup({ item, onClose }: { item: CalendarItem; onClose: () => void }) {
  const isGoogle = item.kind === "google";
  const isLinked = item.kind === "linked";
  return (
    <div className="dom-calendar-overlay" role="presentation" onClick={onClose}>
      <div
        className="dom-calendar-popup"
        role="dialog"
        aria-labelledby="cal-popup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="popup-close" onClick={onClose} aria-label="Sluiten">
          ×
        </button>
        <h3 id="cal-popup-title" className="event-title">
          {isLinked && <LinkIcon className="cal-link-icon cal-link-icon-popup" />}
          {item.title}
        </h3>
        <div className="event-date">
          {parseDateTime(item.start_datetime).toLocaleDateString("nl-NL", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </div>
        <div className="event-time">{formatTimeRange(item.start_datetime, item.end_datetime)}</div>
        {isLinked && item.google_start_datetime && item.google_end_datetime && (
          <div className="event-time event-time-google-block">
            Zaalrooster (Google):{" "}
            {formatTimeRange(item.google_start_datetime, item.google_end_datetime)}
          </div>
        )}
        {item.room_name && (
          <div className="event-location">
            {isGoogle || isLinked ? "Google Agenda" : "Zaal"}: {item.room_name}
          </div>
        )}
        {item.location_name && <div className="event-location">{item.location_name}</div>}
        {item.description && <div className="event-description">{item.description}</div>}
        {(item.rooms?.length ?? 0) > 0 && (
          <p className="event-type-hint">
            Zalen: {item.rooms!.map((r) => r.name).join(", ")}
          </p>
        )}
        {item.kind === "event" && <p className="event-type-hint">Evenement</p>}
        {isRecurringItem(item) && (
          <p className="event-type-hint">Terugkerend evenement</p>
        )}
        {item.kind === "google" && (
          <p className="event-type-hint event-type-google">Google Agenda (alleen-lezen)</p>
        )}
        {item.kind === "linked" && (
          <p className="event-type-hint event-type-linked">
            Gekoppeld: evenement + zaalrooster in Google Agenda
          </p>
        )}
      </div>
    </div>
  );
}
